import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import axios from 'axios';
import { APIURL } from '../../config';
import moment from 'moment';
import './BillingSummaryChart.css';

const COLORS = {
    shiftHours: '#3FA7D6',
    billedHours: '#FFD166',
    paidHours: '#06D6A0',
};

const MONTHS_TO_SHOW = 6;

export default function BillingSummaryChart({ user, comp_id }) {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);
    const [dimensions, setDimensions] = useState(null);
    const observerRef = useRef(null);

    const containerRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        if (node) {
            const resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const { width, height } = entry.contentRect;
                    setDimensions({
                        width: Math.max(width * 0.8, 200),
                        height: Math.max(height * 0.8, 100),
                    });
                }
            });
            resizeObserver.observe(node);
            observerRef.current = resizeObserver;
        }
    }, []);

    useEffect(() => {
        fetchBillingData();
    }, []);

    const fetchBillingData = async () => {
        try {
            const endDate = moment().endOf('month').format('YYYY-MM-DD');
            const startDate = moment().subtract(MONTHS_TO_SHOW - 1, 'months').startOf('month').format('YYYY-MM-DD');

            const res = await axios.get(`${APIURL}/home/billingSummary`, {
                params: { comp_id, startDate, endDate }
            });

            setChartData(res.data || []);
        } catch (err) {
            console.error('Error fetching billing summary data:', err);
            setChartData([]);
        } finally {
            setLoading(false);
        }
    };

    const isEmpty = chartData.length === 0 || chartData.every((d) => d.shiftHours === 0 && d.billedHours === 0 && d.paidHours === 0);

    return (
        <div className="billing-summary-container">
            <div className="billing-summary-header">
                <span className="billing-summary-header-title">
                    <i className="fas fa-file-invoice-dollar" style={{ color: '#3FA7D6' }} />
                    Billing Summary
                </span>
            </div>
            {loading ? (
                <div className="billing-summary-loading">
                    <CircularProgress size={32} />
                </div>
            ) : isEmpty ? (
                <div className="billing-summary-empty">
                    <i className="fas fa-chart-bar" style={{ fontSize: 28, color: '#ccc' }} />
                    <span>No billing data available</span>
                </div>
            ) : (
                <>
                    <div className="billing-summary-body" ref={containerRef}>
                        {dimensions && <BarChart
                            width={dimensions.width}
                            height={dimensions.height}
                            series={[
                                { data: chartData.map((d) => d.shiftHours), label: 'Shift Hours', color: COLORS.shiftHours },
                                { data: chartData.map((d) => d.billedHours), label: 'Billed Hours', color: COLORS.billedHours },
                                { data: chartData.map((d) => d.paidHours), label: 'Paid Hours', color: COLORS.paidHours },
                            ]}
                            xAxis={[{ data: chartData.map((d) => d.month), scaleType: 'band' }]}
                            slotProps={{
                                legend: { hidden: true },
                            }}
                            margin={{ top: 10, right: 10, bottom: 24, left: 40 }}
                        />}
                    </div>
                    <div className="billing-summary-legend">
                        <div className="billing-summary-legend-item">
                            <span className="billing-summary-legend-swatch" style={{ backgroundColor: COLORS.shiftHours }} />
                            Shift Hours
                        </div>
                        <div className="billing-summary-legend-item">
                            <span className="billing-summary-legend-swatch" style={{ backgroundColor: COLORS.billedHours }} />
                            Billed Hours
                        </div>
                        <div className="billing-summary-legend-item">
                            <span className="billing-summary-legend-swatch" style={{ backgroundColor: COLORS.paidHours }} />
                            Paid Hours
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
